if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
      this.sectionId = this.dataset.section;
      this.variantChangeUnsubscriber = undefined;
      this.reserveUpdateFrame = null;
    }

    connectedCallback() {
      this.targetElement = document.querySelector(this.dataset.triggerSelector);
      this.productInfoElement = document.querySelector(this.dataset.productInfoSelector);
      this.stickyAtcVariantInput = this.querySelector('.product-variant-id');
      this.stickyQuantityInput = this.querySelector('.quantity__input');
      this.controlsElement = this.querySelector('.product-sticky-mobile__controls');

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize);

      this.mutationObserver = new MutationObserver(this.handleMutations);
      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange.bind(this)
      );

      this.handleResize();
      [150, 600, 1500].forEach((delay) => {
        window.setTimeout(() => this.scheduleControlsReserveUpdate(), delay);
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }

      if (this.reserveUpdateFrame) {
        window.cancelAnimationFrame(this.reserveUpdateFrame);
      }

      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }

      this.resetDocumentOffset();
      this.resetControlsReserve();
    }

    isMobileViewport() {
      return window.innerWidth < 750;
    }

    resetDocumentOffset() {
      document.documentElement.style.paddingBottom = '';
    }

    resetControlsReserve() {
      this.style.setProperty('--sticky-controls-reserve-inline-end', '0px');
    }

    updateDocumentOffset() {
      if (!this.isMobileViewport()) {
        this.resetDocumentOffset();
        return;
      }

      const stickyBottomOffset = parseFloat(window.getComputedStyle(this).bottom) || 0;
      document.documentElement.style.paddingBottom = `${this.offsetHeight + stickyBottomOffset}px`;
    }

    handleResize() {
      if (!this.isMobileViewport()) {
        this.classList.remove('is-visible');
        this.classList.remove('show-summary');
        this.resetDocumentOffset();
        this.resetControlsReserve();
        return;
      }

      this.classList.add('is-visible');
      this.handleScroll();
      this.updateDocumentOffset();
      this.scheduleControlsReserveUpdate();
    }

    handleScroll() {
      if (!this.isMobileViewport()) return;

      this.classList.add('is-visible');

      const triggerElement = this.targetElement || this.productInfoElement;
      const scrolledEnough = window.scrollY > Math.max(180, window.innerHeight * 0.25);
      let infoOutOfView = false;

      if (triggerElement) {
        infoOutOfView = triggerElement.getBoundingClientRect().bottom <= 0;
      }

      this.classList.toggle('show-summary', scrolledEnough && infoOutOfView);
      this.updateDocumentOffset();
      this.scheduleControlsReserveUpdate();
    }

    handleVariantChange({ data: { sectionId, html, variant } }) {
      const mainSectionId = this.sectionId.replace(/^template--\d+__/, '');
      const eventSectionId = sectionId.replace(/^template--\d+__/, '');

      if (mainSectionId !== eventSectionId) return;

      if (this.stickyAtcVariantInput) {
        this.stickyAtcVariantInput.value = variant?.id || '';
        this.stickyAtcVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.updateStickyOptionsFromHTML(html);
      this.updateStickyQuantityFromHTML(html);
      this.scheduleControlsReserveUpdate();
    }

    handleMutations(mutations) {
      const hasExternalMutation = mutations.some(({ target }) => target !== this && !this.contains(target));
      if (!hasExternalMutation) return;

      this.scheduleControlsReserveUpdate();
    }

    scheduleControlsReserveUpdate() {
      if (!this.isMobileViewport() || !this.controlsElement) return;
      if (this.reserveUpdateFrame) return;

      this.reserveUpdateFrame = window.requestAnimationFrame(() => {
        this.reserveUpdateFrame = null;
        this.updateControlsReserve();
      });
    }

    updateControlsReserve() {
      if (!this.isMobileViewport() || !this.controlsElement) {
        this.resetControlsReserve();
        return;
      }

      this.resetControlsReserve();

      const controlsRect = this.controlsElement.getBoundingClientRect();
      const stickyZIndex = parseInt(window.getComputedStyle(this).zIndex, 10) || 0;
      let reserveInlineEnd = 0;

      document.querySelectorAll('body *').forEach((element) => {
        if (element === this || this.contains(element)) return;

        const styles = window.getComputedStyle(element);
        if (styles.position !== 'fixed') return;
        if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) === 0) return;

        const elementZIndex = parseInt(styles.zIndex, 10) || 0;
        if (elementZIndex < stickyZIndex) return;

        const rect = element.getBoundingClientRect();
        if (rect.width < 24 || rect.height < 24) return;
        if (rect.bottom <= 0 || rect.right <= 0 || rect.top >= window.innerHeight || rect.left >= window.innerWidth) return;
        if (rect.left < window.innerWidth * 0.55 || rect.top < window.innerHeight * 0.45) return;

        const overlapsHorizontally = rect.left < controlsRect.right && rect.right > controlsRect.left;
        const overlapsVertically = rect.top < controlsRect.bottom && rect.bottom > controlsRect.top;

        if (!overlapsHorizontally || !overlapsVertically) return;

        reserveInlineEnd = Math.max(reserveInlineEnd, Math.max(0, controlsRect.right - rect.left + 12));
      });

      this.style.setProperty('--sticky-controls-reserve-inline-end', `${Math.ceil(reserveInlineEnd)}px`);
    }

    updateStickyOptionsFromHTML(html) {
      const sourceOptionsContainer = html.querySelector('.selected-variant-details .options-container');
      const destinationOptionsContainer = this.querySelector('.selected-variant-details .options-container');

      if (!sourceOptionsContainer || !destinationOptionsContainer) return;

      const sourceOptions = sourceOptionsContainer.querySelectorAll('.selected-variant-option');
      const destinationOptions = destinationOptionsContainer.querySelectorAll('.selected-variant-option');

      sourceOptions.forEach((sourceOption, index) => {
        if (destinationOptions[index]) {
          destinationOptions[index].textContent = sourceOption.textContent;
        }
      });
    }

    updateStickyQuantityFromHTML(html) {
      const sourceQuantityForm = html.getElementById(`Quantity-Form-${this.sectionId}`);
      const destinationQuantityInput = this.stickyQuantityInput;

      if (!sourceQuantityForm || !destinationQuantityInput) return;

      const sourceQuantityInput = sourceQuantityForm.querySelector('.quantity__input');
      if (!sourceQuantityInput) return;

      ['data-cart-quantity', 'data-min', 'data-max', 'step', 'min', 'max'].forEach((attribute) => {
        const value = sourceQuantityInput.getAttribute(attribute);

        if (value === null) {
          destinationQuantityInput.removeAttribute(attribute);
          return;
        }

        destinationQuantityInput.setAttribute(attribute, value);
      });

      destinationQuantityInput.value = sourceQuantityInput.value;
      destinationQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}
